import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import AccountMenu, { SignupLink } from "@/components/AccountMenu";

const LINKS = [
  { label: "Challenges", to: "/challenges", testId: "nav-link-challenges" },
  { label: "Events", to: "/events", testId: "nav-link-events" },
  { label: "Expeditions", to: "/expeditions", testId: "nav-link-expeditions" },
  { label: "Journal", to: "/journal", testId: "nav-link-journal" },
  { label: "Articles", to: "/articles", testId: "nav-link-articles" },
  { label: "Training", to: "/training", testId: "nav-link-training" },
  { label: "Community", to: "/community", testId: "nav-link-community" },
  { label: "About Us", to: "/about", testId: "nav-link-about" },
  { label: "Leadership", to: "/leadership", testId: "nav-link-leadership" },
  { label: "Gear Room", to: "/gallery", testId: "nav-link-gallery" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-[#FBF9F5]/80 border-b border-[#0D1117]/10"
      data-testid="nav-bar"
    >
      <nav className="px-6 sm:px-12 lg:px-10 2xl:px-20 h-16 sm:h-20 flex items-center justify-between">
        <a href="/" data-testid="nav-brand-logo" className="group flex items-center">
          <img src="/logo-horizontal.png" alt="Fitygrity" className="h-8 sm:h-9 w-auto object-contain rounded-md transition-opacity duration-300 group-hover:opacity-80" />
        </a>
        <div className="hidden lg:flex items-center gap-5">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className="font-mono text-[11px] uppercase tracking-[0.13em] text-[#374151] hover:text-[#FF4E00] transition-colors duration-300 whitespace-nowrap"
            >
              {l.label}
            </Link>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/ask"
            data-testid="nav-link-ask"
            className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#FF4E00] hover:text-[#E04000] transition-colors duration-300 whitespace-nowrap"
          >
            Ask AI
          </Link>
          <SignupLink
            testId="nav-link-signup"
            className="inline-flex items-center justify-center bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5] font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 rounded-full transition-colors duration-300 whitespace-nowrap"
          />
          <AccountMenu />
          <Link
            to="/contact"
            data-testid="nav-enquire-button"
            className="group inline-flex items-center gap-2 border border-[#FF4E00]/50 hover:border-[#FF4E00] text-[#FF4E00] font-mono text-xs uppercase tracking-[0.2em] px-5 py-2.5 rounded-full transition-all duration-300 hover:bg-[#FF4E00] hover:text-[#FBF9F5]"
          >
            Contact
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </div>
        <button
          className="lg:hidden text-[#0D1117] p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden border-t border-[#0D1117]/10 bg-[#FBF9F5] overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-[#374151] hover:text-[#FF4E00] transition-colors"
                >
                  {l.label}
                </Link>
              ))}
              <Link to="/ask" onClick={() => setOpen(false)} className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF4E00]">
                Ask AI
              </Link>
              <SignupLink
                testId="nav-mobile-signup"
                onNavigate={() => setOpen(false)}
                className="inline-flex items-center justify-center bg-[#FF4E00] text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-5 py-3.5 rounded-full"
              />
              <AccountMenu mobile onNavigate={() => setOpen(false)} />
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-2 bg-[#FF4E00] text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-5 py-3.5 rounded-full"
              >
                Contact <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
